import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Tooltip, useMediaQuery, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Typography, Stack } from '@mui/material';
import { makeStyles } from '@mui/styles';
import HelpIcon from '@mui/icons-material/Help';
import historyDetailsIcon from '../../../icons/paymentHistoryDetailsIcon.png';
import { css } from '@emotion/react';
import { HashLoader } from 'react-spinners';
import ApplicationDetailModal from './ApplicationDetailModal';
import { ApplicationOccupantSummaryDto, ApplicationOccupantSummaryDtoStatusEnum, ApplicationSummaryDto, TransactionDTO } from '@inosio/payments-sdk';
import { getTransactionHistory } from '../../payments/sagas/residentpaymentsSlice';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { useHistory } from 'react-router';
import { resetEntireApplicantState, fetchApplications, selectApplicantsState } from '../sagas/applicants/applicantsSlice';
import ApplicationHistoryModal from './ApplicationHistoryModal';
import { clearExportApplicationDetailStates } from '../sagas/pm/applicationsSlice';

const useStyles = makeStyles((theme) => ({
  labelText: {
    fontFamily: 'Open Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '700 !important',
    fontSize: '18px !important',
    lineHeight: '25px !important',
    color: '#14142B',
  },
  submitBtnText: {
    fontFamily: 'Work Sans !important',
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#39A2DB !important',
  },
  cardContainer: {
    width: '100%',
    height: 352,
    border: '3px solid #F1F0F5',
    borderRadius: '24px',
  },
  cardTopContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 74,
    borderBottom: '3px solid #F1F0F5',
  },
  statusText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '500 !important',
    fontSize: '14px !important',
    lineHeight: '24px !important',
  },
  cardLabelText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
  },
  cardBodyText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '18px !important',
    lineHeight: '24px !important',
    color: '#000000 !important',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  grayLabelText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#A8A8A8 !important',
  },
}));

/**
 * This is the applications dashboard ui for the applicants. It will display a table of all applications this appicant has.
 * @returns
 */
export default function ApplicantDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const matches1200 = useMediaQuery('(max-width:1200px)');
  const dispatch = useDispatch();
  const { ipaUser } = useSelector(selectAuthState);
  const { isFetchingApplications, applicationsFetchResponse } = useSelector(selectApplicantsState);

  const [selectedApp, setSelectedApp] = useState<ApplicationSummaryDto | undefined>(undefined);
  const [isOpenAppHistoryModal, setIsOpenAppHistoryModal] = useState(false);
  const [isOpenAppDetailModal, setIsOpenAppDetailModal] = useState(false);
  const [applicationPayment, setApplicationPayment] = useState<TransactionDTO | null>(null);
  const [selectedAppPrimaryApplicant, setSelectedAppPrimaryApplicant] = useState<ApplicationOccupantSummaryDto | undefined>(undefined);

  useEffect(() => {
    //must be on the main dashboard page.
    //reset state
    dispatch(resetEntireApplicantState());

    if (ipaUser?.currentProfile.uuid) {
      //fetch all apps.
      dispatch(
        fetchApplications({
          applicantUuid: ipaUser?.currentProfile.uuid,
        }),
      );
    }
  }, [ipaUser]);

  useEffect(() => {
    dispatch(getTransactionHistory());
  }, [dispatch]);

  const override = css`
    display: block;
    margin: 100px auto;
  `;

  const viewAppHistory = (app: ApplicationSummaryDto) => {
    const primaryApplicant = app?.Applicants.find((a) => a.Uuid === app?.PrimaryApplicantUuid);
    setSelectedAppPrimaryApplicant(primaryApplicant);
    setSelectedApp(app);
    setIsOpenAppHistoryModal(true);
  };

  // Callback function to open the ApplicationDetailModal
  const openAppDetailModal = (payment: TransactionDTO) => {
    setApplicationPayment(payment);
    setIsOpenAppDetailModal(true);
    setIsOpenAppHistoryModal(false);
  };

  //looks like this answers "does this org still do apps with inosio"
  const checkAppConfigStatus = (appUuid: string) => {
    return true;
  };

  const getThisUsersStatusOnApp = (application: ApplicationSummaryDto) => {
    //now find this specific user.
    const thisApplicant = application.Applicants.find((a) => a.Uuid === ipaUser?.currentProfile.uuid);
    return thisApplicant?.Status || null;
  };

  const getApplicationStatusText = (appDto: ApplicationSummaryDto) => {
    /*
    Options:
    - Submitted
    - Rejected
    - Accepted
    - Incomplete
    - Incomplete, waiting on others

     */

    const currentUsersStatusOnThisApp = getThisUsersStatusOnApp(appDto);

    const isCurrentUserThePrimary = appDto.PrimaryApplicantUuid === ipaUser?.currentProfile.uuid;
    switch (appDto.Status) {
      case 'INCOMPLETE':
        if (currentUsersStatusOnThisApp === ApplicationOccupantSummaryDtoStatusEnum.Completed && !isCurrentUserThePrimary) return 'Submitted, waiting on others';
        else return 'Incomplete';
      case 'SUBMITTED':
        return 'Submitted';
      case 'REJECTED':
        return 'Rejected';
      case 'APPROVED':
        return 'Approved';
      case 'RETURNED':
        if (currentUsersStatusOnThisApp === ApplicationOccupantSummaryDtoStatusEnum.Completed && !isCurrentUserThePrimary) return 'Submitted, waiting on others';
        else return 'Returned';
      default:
        return '';
    }
  };

  return (
    <Box>
      <Box sx={{ background: '#FFFFFF', borderRadius: '24px', height: 370, pt: 2 }}>
        <Typography sx={{ mb: 2, ml: 2 }} className={classes.labelText}>
          Application
          <Tooltip title="View Applications that are pending or have been submitted." placement="right">
            <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
          </Tooltip>
        </Typography>
        {isFetchingApplications ? (
          <Box sx={{ width: '100%' }}>
            <HashLoader color={'#D3D3D3'} loading={true} size={100} css={override} />
          </Box>
        ) : (
          <TableContainer style={{ flex: 1, minHeight: 265 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ borderTop: '0.5px solid #F1F0F5' }}>
                  {!matchesMobile && (
                    <TableCell id="subTitle" sx={{ py: '12.5px' }}>
                      Name
                    </TableCell>
                  )}
                  <TableCell id="subTitle" sx={{ py: '12.5px' }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      Company
                    </Stack>
                  </TableCell>
                  {!matchesMobile && !matches1200 && (
                    <TableCell id="subTitle" sx={{ py: '12.5px' }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        Property
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell id="subTitle" sx={{ py: '12.5px' }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      Application Status
                    </Stack>
                  </TableCell>
                  <TableCell id="subTitle" sx={{ py: '12.5px' }}></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {applicationsFetchResponse?.map((app) => {
                  const primaryApplicant = app.Applicants.find((a) => a.Uuid === app.PrimaryApplicantUuid);
                  const isCurrentUserThePrimary = app.PrimaryApplicantUuid === ipaUser?.currentProfile.uuid;
                  const primaryApplicantText = isCurrentUserThePrimary
                    ? null
                    : `${primaryApplicant?.FirstName}${primaryApplicant?.MiddleName ? ' '.concat(primaryApplicant?.MiddleName) : ''} ${primaryApplicant?.LastName}`;
                  const thisUsersApplicant = app.Applicants.find((ca) => ca.Uuid === ipaUser?.currentProfile.uuid);
                  const currentUsersStatusOnThisApp = thisUsersApplicant?.Status;
                  const appStatusText = getApplicationStatusText(app);
                  return (
                    <TableRow key={app.Uuid}>
                      {!matchesMobile && (
                        <TableCell id="content" sx={{ height: '42px' }}>
                          <Box sx={{ width: '100%', pr: 6 }}>
                            <Typography sx={{ ml: 3 }}>
                              {thisUsersApplicant?.FirstName} {thisUsersApplicant?.MiddleName} {thisUsersApplicant?.LastName}
                            </Typography>
                            {primaryApplicantText && (
                              <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                {primaryApplicantText}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                      )}
                      <TableCell id="content" sx={{ height: '42px' }}>
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} textAlign="center">
                          {app.OrganizationName ?? ''}
                        </Stack>
                      </TableCell>
                      {!matchesMobile && !matches1200 && (
                        <TableCell id="content" sx={{ height: '42px' }}>
                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            {app.PropertyAddress ?? ''}
                          </Stack>
                        </TableCell>
                      )}
                      <TableCell id="content" sx={{ height: '42px' }}>
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} textAlign="center">
                          {appStatusText}
                          {['Submitted', 'Rejected', 'Approved'].includes(appStatusText) && isCurrentUserThePrimary && (
                            <img style={{ cursor: 'pointer', marginLeft: '5px' }} src={historyDetailsIcon} alt="details-icon" onClick={() => viewAppHistory(app)} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell id="content" sx={{ height: '42px' }}>
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                          {['incomplete', 'returned'].includes(app.Status?.toLocaleLowerCase()) && (
                            <Button
                              variant="outlined"
                              sx={{
                                border: '1px solid #39A2DB',
                                borderRadius: '24px',
                                padding: '2px 30px',
                              }}
                              disabled={!checkAppConfigStatus(app.Uuid)}
                              onClick={() => {
                                history.push(`/resui/applications/${app.Uuid}`);
                              }}
                            >
                              <Typography sx={{ textTransform: 'capitalize' }} className={classes.submitBtnText}>
                                {currentUsersStatusOnThisApp === ApplicationOccupantSummaryDtoStatusEnum.Completed || app.Status === 'RETURNED' ? 'Update' : 'Continue'}
                              </Typography>
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {isOpenAppDetailModal && applicationPayment && (
        <ApplicationDetailModal
          open={isOpenAppDetailModal}
          closeCallback={() => {
            setIsOpenAppDetailModal(false);
            dispatch(clearExportApplicationDetailStates());
          }}
          appUuid={selectedApp?.Uuid ?? ''}
          applicantUuid={selectedApp?.PrimaryApplicantUuid!}
          paymentID={applicationPayment?.TransactionUuid ?? ''}
          appStatus={selectedApp?.Status?.toLocaleLowerCase() ?? ''}
          companyName={selectedApp?.OrganizationName ?? ''}
          propertyName={selectedApp?.PropertyAddress ?? ''}
          applicantName={`${selectedAppPrimaryApplicant?.FirstName} ${selectedAppPrimaryApplicant?.MiddleName || ''} ${selectedAppPrimaryApplicant?.LastName}`}
          paidAmount={
            applicationPayment?.TransactionDetails?.map((transactionDetail) => {
              if (transactionDetail.EntityPaymentTypeId === 1) return 0;
              return transactionDetail.Amount;
            }).reduce((prev, nex) => prev + nex, 0) ?? 0
          }
          totalAmount={applicationPayment?.Amount ?? 0}
          transactionDate={applicationPayment?.TransactionDate ?? ''}
          paymentFees={applicationPayment?.TransactionDetails?.filter((transactionDetail) => transactionDetail.EntityPaymentTypeId === 1)?.[0]?.Amount ?? 0}
          last4={applicationPayment?.TransactionDetails?.[0]?.FromPayMethod?.AccountLastFour ?? ''}
        />
      )}
      {isOpenAppHistoryModal && (
        <ApplicationHistoryModal
          open={isOpenAppHistoryModal}
          onClose={() => {
            setIsOpenAppHistoryModal(false);
          }}
          appSummaryObj={selectedApp}
          clickViewReceiptCallback={openAppDetailModal}
        />
      )}
    </Box>
  );
}
